import React, { useState } from 'react';
import { Box, Text, HStack, VStack, Button, Popover, PopoverTrigger, PopoverContent, PopoverBody, PopoverArrow, PopoverCloseButton, Checkbox, Tag, TagLabel, TagCloseButton, Select, Link } from '@chakra-ui/react';
import PageTitle from '../components/PageTitle';
import { useMyLeagueStore } from '../store/myLeagueStore';
import { useTransactions, useTransactionTypes, DateRangeOption } from '../hooks/useTransactions';
import { Spinner, Center, Table, Thead, Tbody, Tr, Th, Td } from '@chakra-ui/react';
import { getTeamNameById } from '../utils/getTeamInfo';
import TradeModal from '../components/TradeModal';
import SignFreeAgentModal from '../components/SignFreeAgentModal';
import { useUserContext } from '../contexts/UserContext';
import { useDeals } from '../hooks/useDeals';
import apiClient from '../lib/api/apiClient';

type Chip = { type: 'transactionType' | 'team' | 'dateRange'; label: string; value?: string };

const Transactions: React.FC = () => {
  // Filter state
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState<number[]>([]);
  const [dateRange, setDateRange] = useState<DateRangeOption>('all');
  const [typePopoverOpen, setTypePopoverOpen] = useState(false);
  const [teamPopoverOpen, setTeamPopoverOpen] = useState(false);

  // Get current league
  const myLeague = useMyLeagueStore(state => state.myLeague);
  const leagueId = myLeague?.id;

  // Get current user's team
  const { myTeam } = useUserContext();

  // Get all teams from league for team filter
  const allTeams = React.useMemo(() => {
    if (!myLeague?.conferences) return [];
    const teams: Array<{ id: number; name: string }> = [];
    myLeague.conferences.forEach(conf => {
      conf.divisions.forEach(div => {
        div.teams.forEach(team => {
          teams.push({ id: team.id, name: team.name });
        });
      });
    });
    return teams.sort((a, b) => a.name.localeCompare(b.name));
  }, [myLeague]);

  // Track current page and accumulated transactions
  const [currentPage, setCurrentPage] = React.useState(0);
  const [accumulatedTransactions, setAccumulatedTransactions] = React.useState<any[]>([]);
  const [totalPages, setTotalPages] = React.useState(0);

  // Create a stable filter key to detect filter changes
  const filterKey = React.useMemo(() =>
    JSON.stringify({ selectedTypes, selectedTeamIds, dateRange }),
    [selectedTypes, selectedTeamIds, dateRange]
  );

  // Reset accumulated transactions when filters change
  React.useEffect(() => {
    setCurrentPage(0);
    setAccumulatedTransactions([]);
    setTotalPages(0);
  }, [filterKey]);

  // Load transaction types
  const { transactionTypes, isLoading: typesLoading } = useTransactionTypes();
  const { getDealById } = useDeals(leagueId);

  // Calculate hours back from date range selection
  const hoursBack = React.useMemo(() => {
    switch (dateRange) {
      case 'today':
        return 24; // Last 24 hours
      case 'yesterday':
        return 48; // Last 48 hours (to cover all of yesterday)
      case 'last3days':
        return 72; // Last 72 hours (3 days)
      case 'thisweek':
        return 168; // Last 168 hours (7 days)
      case 'thismonth':
        return 744; // Last 744 hours (31 days)
      default:
        return undefined; // 'all' - no date filtering
    }
  }, [dateRange]);

  // Load transactions with all filters (backend handles filtering)
  const {
    data: transactionsData,
    isLoading: transactionsLoading,
    error: transactionsError,
    refetch: refetchTransactions,
    isFetching,
  } = useTransactions({
    leagueId: leagueId || 0,
    filters: {
      type: selectedTypes.length > 0 ? selectedTypes : undefined,
      teamIds: selectedTeamIds.length > 0 ? selectedTeamIds : undefined,
      hoursBack: hoursBack,
      myTeam: myTeam ? Number(myTeam) : undefined,
    },
    page: currentPage,
    pageSize: 50,
  });

  // Accumulate transactions as pages load
  React.useEffect(() => {
    if (transactionsData) {
      const newTransactions = transactionsData.transactions || [];
      setTotalPages(transactionsData.totalPages || 0);

      if (currentPage === 0) {
        setAccumulatedTransactions(newTransactions);
      } else if (currentPage > 0) {
        setAccumulatedTransactions(prev => {
          if (prev.length === 0) {
            return newTransactions;
          }
          return [...prev, ...newTransactions];
        });
      }
    }
  }, [transactionsData, currentPage]);

  // Get transactions from accumulated list
  const filteredTransactions = accumulatedTransactions;

  // Handle "Show More" click
  const handleShowMore = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(prev => prev + 1);
    }
  };

  // Remove a filter chip
  const removeChip = (type: string, value?: string) => {
    if (type === 'transactionType' && value) {
      setSelectedTypes(selectedTypes.filter(t => t !== value));
    }
    if (type === 'team' && value) {
      setSelectedTeamIds(selectedTeamIds.filter(id => id !== Number(value)));
    }
    if (type === 'dateRange') {
      setDateRange('all');
    }
  };

  // Transaction type multi-select logic
  const toggleType = (type: string) => {
    setSelectedTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
  };

  // Team multi-select logic
  const toggleTeam = (teamId: number | string) => {
    const id = typeof teamId === 'string' ? parseInt(teamId) : teamId;
    setSelectedTeamIds(prev => prev.includes(id) ? prev.filter(selectedId => selectedId !== id) : [...prev, id]);
  };

  // Get display name for transaction type
  const getTypeDisplayName = (type: string) => {
    if (!Array.isArray(transactionTypes)) return type;
    const typeObj = transactionTypes.find(t => t.type === type);
    return typeObj?.displayName || type;
  };

  // Get unique team IDs from a transaction
  const getUniqueTeamIds = (transaction: any): number[] => {
    // For settlement_complete, only show the primary team (no second team)
    if (transaction.type === 'settlement_complete') {
      return transaction.team_id ? [transaction.team_id] : [];
    }
    const teamIds: number[] = [];
    if (transaction.team_id) {
      teamIds.push(transaction.team_id);
    }
    if (transaction.teams && Array.isArray(transaction.teams)) {
      teamIds.push(...transaction.teams);
    }
    return [...new Set(teamIds)]; // Remove duplicates
  };


  // Trade Modal state
  const [selectedDealId, setSelectedDealId] = useState<number | null>(null);
  const [isTradeModalOpen, setIsTradeModalOpen] = useState(false);

  // Free Agent Modal state
  const [selectedFreeAgentDealId, setSelectedFreeAgentDealId] = useState<number | null>(null);
  const [selectedFreeAgentPlayer, setSelectedFreeAgentPlayer] = useState<any | null>(null);
  const [selectedWinningTeamId, setSelectedWinningTeamId] = useState<number | undefined>(undefined);
  const [isFreeAgentModalOpen, setIsFreeAgentModalOpen] = useState(false);

  // Handle opening free agent modal
  const handleOpenFreeAgentDetails = async (dealId: number) => {
    console.log("handleOpenFreeAgentDetails called with dealId:", dealId);
    try {
      const dealData = await getDealById(dealId);

      let player;
      if (dealData && dealData.players1Details && dealData.players1Details.length > 0) {
        player = dealData.players1Details[0];
      } else if (dealData && (dealData as any).playerofinterest && dealData.team1) {
        // Fallback: if players1Details is missing, try to find the player in the signing team's roster
        // Since providing a specific player ID endpoint failed (404), we'll fetch the team's roster
        try {
          console.log("Fetching team roster for team:", dealData);
          const foundPlayer = await apiClient.get(`/player/rw-player-from-rosterid/${(dealData as any).playerofinterest}`)
          if (foundPlayer) {
            console.log("Found player in team roster:", foundPlayer);
            player = foundPlayer.data;
          } else {
            // If not on roster (maybe traded away?), try research endpoint or just error out
            console.warn("Player not found in team roster");
          }
        } catch (err) {
          console.error("Error fetching team roster:", err);
        }
      }

      if (player) {
        // Cast to any to allow accessing properties that might be missing from the strict type definition
        // but present in the API response data at runtime
        const p = player as any;

        // Check if we have the nested structure or flat structure
        const rwPlayer = p.rwPlayer || {
          id: p.id,
          first_name: p.first_name,
          last_name: p.last_name,
          position_1: p.position_1,
          position_2: p.position_2,
          position_3: p.position_3,
          image: p.image,
          bats: p.bats,
          throws: p.throws
        };

        const formattedPlayer = {
          ...p,
          rwPlayer: rwPlayer,
          playerContract: p.playerContract || p.contract || {
            current_salary: 0,
            contractYears: []
          }
        };

        setSelectedFreeAgentPlayer(formattedPlayer);
        setSelectedFreeAgentDealId(dealId);
        if (dealData && dealData.team1) {
          setSelectedWinningTeamId(dealData.team1);
        } else {
          setSelectedWinningTeamId(undefined);
        }

        setIsFreeAgentModalOpen(true);
      } else {
        console.error("No player data found for deal:", dealId);
      }
    } catch (error) {
      console.error("Error fetching deal details:", error);
    }
  };

  const handleCloseFreeAgentModal = () => {
    setIsFreeAgentModalOpen(false);
    setSelectedFreeAgentDealId(null);
    setSelectedFreeAgentPlayer(null);
    setSelectedWinningTeamId(undefined);
  };

  // Handle opening details (dispatch based on type if needed, though currently link handles it)
  // We'll update rendering to call this
  const handleViewDetailsClick = (transaction: any) => {
    console.log("handleViewDetailsClick called:", transaction);
    if (transaction.type === 'free_agent_signed' && transaction.deal_id) {
      console.log("Opening free agent modal for deal:", transaction.deal_id);
      handleOpenFreeAgentDetails(transaction.deal_id);
    } else if (transaction.deal_id) {
      console.log("Opening trade modal for deal:", transaction.deal_id);
      handleOpenDetails(transaction.deal_id);
    } else {
      console.warn("No deal_id found regarding transaction:", transaction);
    }
  };

  // Handle opening trade modal
  const handleOpenDetails = async (dealId: number) => {
    setSelectedDealId(dealId);
    setIsTradeModalOpen(true);
  };

  const handleCloseTradeModal = () => {
    setIsTradeModalOpen(false);
    setSelectedDealId(null);
  };

  // Render filter chips
  const chips: Chip[] = [
    ...selectedTypes.map(type => ({ type: 'transactionType', label: getTypeDisplayName(type), value: type })),
    ...selectedTeamIds.map(teamId => ({ type: 'team', label: getTeamNameById(teamId, myLeague) || `Team ${teamId}`, value: teamId.toString() })),
    dateRange !== 'all' ? { type: 'dateRange', label: `Date: ${dateRange}` } : undefined,
  ].filter((chip): chip is Chip => Boolean(chip));

  const dateRangeOptions: { value: DateRangeOption; label: string }[] = [
    { value: 'all', label: 'All Dates' },
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last3days', label: 'Last 3 Days' },
    { value: 'thisweek', label: 'This Week (7 days)' },
    { value: 'thismonth', label: 'This Month (31 days)' },
  ];

  return (
    <Box className="minimal-panel-padding">
      <PageTitle mb={4}>Transactions</PageTitle>
      {/* Filter Bar */}
      <VStack align="flex-start" spacing={3} mb={2}>
        <HStack spacing={4}>
          {/* Transaction Type Multi-Select */}
          <Popover
            isOpen={typePopoverOpen}
            onClose={() => setTypePopoverOpen(false)}
            placement="bottom-start"
          >
            <PopoverTrigger>
              <Button onClick={() => setTypePopoverOpen(!typePopoverOpen)} variant="outline" colorScheme="blue" size="sm">
                {selectedTypes.length > 0 ? `${selectedTypes.length} Type${selectedTypes.length > 1 ? 's' : ''}` : 'Select Transaction Types'}
              </Button>
            </PopoverTrigger>
            <PopoverContent bg="gray.800" color="white" minW="250px" maxH="400px" overflowY="auto">
              <PopoverArrow />
              <PopoverCloseButton />
              <PopoverBody>
                {typesLoading ? (
                  <Center py={4}><Spinner size="sm" /></Center>
                ) : (
                  <VStack align="flex-start" spacing={1}>
                    {Array.isArray(transactionTypes) && transactionTypes.length > 0 ? (
                      transactionTypes.map(type => (
                        <Checkbox
                          key={type.type}
                          isChecked={selectedTypes.includes(type.type)}
                          onChange={() => toggleType(type.type)}
                          colorScheme="blue"
                        >
                          {type.displayName}
                        </Checkbox>
                      ))
                    ) : (
                      <Text color="gray.400" fontSize="sm">No transaction types available</Text>
                    )}
                  </VStack>
                )}
              </PopoverBody>
            </PopoverContent>
          </Popover>

          {/* Team Multi-Select */}
          <Popover
            isOpen={teamPopoverOpen}
            onClose={() => setTeamPopoverOpen(false)}
            placement="bottom-start"
          >
            <PopoverTrigger>
              <Button onClick={() => setTeamPopoverOpen(!teamPopoverOpen)} variant="outline" colorScheme="blue" size="sm">
                {selectedTeamIds.length > 0 ? `${selectedTeamIds.length} Team${selectedTeamIds.length > 1 ? 's' : ''}` : 'Select Teams'}
              </Button>
            </PopoverTrigger>
            <PopoverContent bg="gray.800" color="white" minW="250px" maxH="400px" overflowY="auto">
              <PopoverArrow />
              <PopoverCloseButton />
              <PopoverBody>
                <VStack align="flex-start" spacing={1}>
                  {allTeams.map(team => (
                    <Checkbox
                      key={team.id}
                      isChecked={selectedTeamIds.includes(team.id)}
                      onChange={() => toggleTeam(team.id)}
                      colorScheme="blue"
                    >
                      {team.name}
                    </Checkbox>
                  ))}
                </VStack>
              </PopoverBody>
            </PopoverContent>
          </Popover>

        </HStack>
        {/* Filter Chips */}
        <HStack spacing={2} flexWrap="wrap">
          {chips.map((chip, idx) => (
            <Tag key={idx} size="md" colorScheme="blue" borderRadius="full">
              <TagLabel>{chip.label}</TagLabel>
              <TagCloseButton onClick={() => removeChip(chip.type, chip.value)} />
            </Tag>
          ))}
        </HStack>
      </VStack>

      {/* Transactions Table */}
      <Box mt={8}>
        {transactionsLoading && currentPage === 0 ? (
          <Center py={10}><Spinner size="xl" /></Center>
        ) : transactionsError ? (
          <Text color="red.400">Error loading transactions.</Text>
        ) : leagueId ? (
          <>
            <Box width="100%" overflowX="auto" className="tight-table-padding">
              <Table
                variant="simple"
                size="sm"
                bg="whiteAlpha.100"
                borderRadius="md"
                width="auto"
                minW="640px"
              >
                <Thead>
                  <Tr>
                    <Th color="white">Date</Th>
                    <Th color="white" display={{ base: 'none', md: 'table-cell' }}>Type</Th>
                    <Th color="white">Transaction</Th>
                    <Th color="white">Team(s)</Th>
                    <Th color="white">Details</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredTransactions.length === 0 ? (
                    <Tr>
                      <Td colSpan={5} textAlign="center" color="gray.400">No transactions found.</Td>
                    </Tr>
                  ) : (
                    filteredTransactions.map((transaction: any) => {
                      const uniqueTeamIds = getUniqueTeamIds(transaction);

                      return (
                        <Tr key={transaction.id}>
                          <Td color="white">
                            <Box display={{ base: 'block', md: 'none' }} mb={1}>
                              <Text fontSize="sm" color="gray.300">
                                {getTypeDisplayName(transaction.type) || '-'}
                              </Text>
                            </Box>
                            {transaction.createdAt || transaction.created_at
                              ? new Date(transaction.createdAt || transaction.created_at).toLocaleDateString()
                              : '-'}
                          </Td>
                          <Td color="white" display={{ base: 'none', md: 'table-cell' }}>
                            {getTypeDisplayName(transaction.type) || '-'}
                          </Td>
                          <Td color="white">{transaction.headline || '-'}</Td>
                          <Td color="white">
                            {uniqueTeamIds.length > 0 ? (
                              <VStack align="flex-start" spacing={1}>
                                {uniqueTeamIds.map((teamId, idx) => (
                                  <Text key={teamId != null ? teamId : `fa-${idx}`} fontSize="sm">
                                    {transaction.type === 'free_agent_signed' && (teamId == null || !getTeamNameById(teamId, myLeague))
                                      ? 'Free Agent'
                                      : (getTeamNameById(teamId, myLeague) || `Team ${teamId}`)}
                                  </Text>
                                ))}
                              </VStack>
                            ) : (
                              '-'
                            )}
                          </Td>
                          <Td color="white">
                            {transaction.deal_id && transaction.type !== 'settlement_complete' ? (
                              <Link
                                color="blue.400"
                                textDecoration="underline"
                                cursor="pointer"
                                onClick={() => handleViewDetailsClick(transaction)}
                              >
                                View Details
                              </Link>
                            ) : (
                              '-'
                            )}
                          </Td>
                        </Tr>
                      );
                    })
                  )}
                </Tbody>
              </Table>
            </Box>
            {totalPages > 0 && currentPage < totalPages - 1 && (
              <Center py={6}>
                <Button
                  onClick={handleShowMore}
                  isLoading={isFetching}
                  loadingText="Loading..."
                  colorScheme="blue"
                  variant="outline"
                  size="md"
                >
                  Show More
                </Button>
              </Center>
            )}
          </>
        ) : (
          <Text color="gray.400">Select a league to view transactions.</Text>
        )}
      </Box>

      {/* Trade Modal */}
      {isTradeModalOpen && selectedDealId && (
        <TradeModal
          isOpen={isTradeModalOpen}
          onClose={handleCloseTradeModal}
          tradeId={selectedDealId.toString()}
        />
      )}

      {/* Free Agent Modal */}
      {isFreeAgentModalOpen && selectedFreeAgentPlayer && (
        <SignFreeAgentModal
          isOpen={isFreeAgentModalOpen}
          onClose={handleCloseFreeAgentModal}
          player={selectedFreeAgentPlayer}
          level="MLB" // Default to MLB for viewing past transactions
          existingDealId={selectedFreeAgentDealId}
          viewOnly={true}
          winningTeamId={selectedWinningTeamId}
        />
      )}
    </Box>
  );
};

export default Transactions;

